import { buildHubupFormUrl, buildHubupNotificationUrl } from '../api/hubup';
import { buildEmailAnchorInlineStyle } from './emailButtonStyles';

export const HUBUP_NOTIFICATION_BUTTON_VARIABLE = '%hubup_notification_button%';
const CRM_HUBUP_EDITOR_START = '<!--crm-hubup-btn-->';
const CRM_HUBUP_EDITOR_END = '<!--/crm-hubup-btn-->';

export function isEmptyCampaignEmailHtml(html) {
  const raw = String(html || '').trim();
  if (!raw) return true;
  const text = raw
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return !text;
}

/** Retire l’ancienne variable closer (contenu édité avant envoi). */
export function stripLegacyMessageCloserVariable(html) {
  return String(html || '').replace(/%message_closer%/g, '');
}

/** HTML canonique (variable HubUP, sans coquille éditeur) pour aperçu / détection. */
export function canonicalCampaignEmailHtml(html) {
  return stripLegacyMessageCloserVariable(
    stripLegacyProductCardVariable(htmlFromCampaignEmailEditor(html)),
  );
}

export function campaignEmailHtmlForPreview(html) {
  if (isEmptyCampaignEmailHtml(html)) {
    return '<p><br></p>';
  }
  return canonicalCampaignEmailHtml(html);
}

export function templateUsesHubupVariable(html) {
  const canonical = canonicalCampaignEmailHtml(html);
  if (canonical.includes(HUBUP_NOTIFICATION_BUTTON_VARIABLE)) return true;
  return /data-hubup-slot\s*=/.test(String(html || ''));
}

/** Retire l’ancienne variable produit du HTML stocké. */
export function stripLegacyProductCardVariable(html) {
  return String(html || '').replace(/%product_card%/g, '');
}

export function buildHubupButtonPreviewHtml(notification) {
  return buildHubupNotificationButtonPreview(notification);
}

function buildHubupEditorChip(notification) {
  const targetUrl =
    notification?.buttonTarget === 'form' && notification?.formUrl
      ? notification.formUrl
      : notification?.url || 'https://hubup.example/notification/apercu';
  const inlineStyle = `${buildEmailAnchorInlineStyle({
    backgroundColor: notification?.buttonColor,
    borderColor: notification?.buttonBorderColor || notification?.buttonColor,
    textColor: notification?.buttonTextColor,
    shape: notification?.buttonShape,
  })}cursor:default;user-select:none;`;
  const label = escapeEmailTemplateHtml(
    normalizeHubupNotificationButtonLabel(notification?.buttonLabel),
  );
  return `<span data-hubup-slot="1" contenteditable="false" style="${inlineStyle}">${label}</span>`;
}

/** Affichage éditeur : la variable HubUP devient le bouton à sa place (compatible Quill). */
export function htmlForCampaignEmailEditor(storedHtml, hubupPreviewNotification) {
  let html = canonicalCampaignEmailHtml(storedHtml);
  if (!html.includes(HUBUP_NOTIFICATION_BUTTON_VARIABLE)) return html;
  const chip = buildHubupEditorChip(hubupPreviewNotification);
  return html.replace(/%hubup_notification_button%/g, chip);
}

/** Enregistrement éditeur : le bouton redevient la variable. */
export function htmlFromCampaignEmailEditor(editorHtml) {
  let out = String(editorHtml || '');
  out = out.replace(
    /<span[^>]*data-hubup-slot="1"[^>]*>[\s\S]*?<\/span>/gi,
    HUBUP_NOTIFICATION_BUTTON_VARIABLE,
  );
  out = out.replace(
    /<!--crm-hubup-btn-->[\s\S]*?<!--\/crm-hubup-btn-->/g,
    HUBUP_NOTIFICATION_BUTTON_VARIABLE,
  );
  out = stripLegacyProductCardVariable(out);
  if (
    !out.includes(HUBUP_NOTIFICATION_BUTTON_VARIABLE) &&
    /hubup_notification_button/i.test(out)
  ) {
    out = out.replace(
      /%?\s*hubup_notification_button\s*%/gi,
      HUBUP_NOTIFICATION_BUTTON_VARIABLE,
    );
  }
  return out;
}

/** Aperçu éditeur : bouton HubUP stylé (URL fictive) tant qu’aucune notif n’est liée à l’envoi. */
export function buildEditorHubupPreviewFromDesign(design = {}) {
  return {
    url: 'https://hubup.example/notification/apercu',
    formUrl: 'https://hubup.example/form/apercu',
    buttonTarget: DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
    buttonLabel: design.buttonLabel || DEFAULT_HUBUP_NOTIFICATION_BUTTON_LABEL,
    buttonColor: design.buttonColor || DEFAULT_HUBUP_NOTIFICATION_BUTTON_COLOR,
    buttonBorderColor:
      design.buttonBorderColor || design.buttonColor || DEFAULT_HUBUP_NOTIFICATION_BUTTON_COLOR,
    buttonTextColor: design.buttonTextColor || '',
    buttonShape: design.buttonShape || 'rounded',
  };
}

export function previewHubupNotificationForEmail() {
  return {
    url: 'https://hubup.example/notification/apercu',
    formUrl: 'https://hubup.example/form/apercu',
    buttonTarget: 'notification',
    buttonLabel: DEFAULT_HUBUP_NOTIFICATION_BUTTON_LABEL,
    buttonColor: DEFAULT_HUBUP_NOTIFICATION_BUTTON_COLOR,
  };
}
export const DEFAULT_HUBUP_NOTIFICATION_BUTTON_LABEL = 'HubUP';
export const DEFAULT_HUBUP_NOTIFICATION_BUTTON_COLOR = '#D2FA94';
export const DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET = 'notification';

export function escapeEmailTemplateHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return String(text ?? '').replace(/[&<>"']/g, (m) => map[m]);
}

export function normalizeHubupNotificationButtonLabel(value) {
  const label = String(value ?? '').trim();
  return label || DEFAULT_HUBUP_NOTIFICATION_BUTTON_LABEL;
}

export function normalizeHubupNotificationButtonColor(value) {
  const color = String(value ?? '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(color)
    ? color
    : DEFAULT_HUBUP_NOTIFICATION_BUTTON_COLOR;
}

function readableTextColor(backgroundColor) {
  const hex = normalizeHubupNotificationButtonColor(backgroundColor).replace(
    '#',
    '',
  );
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111827' : '#ffffff';
}

function buildHubupNotificationButtonPreview(notification) {
  const targetUrl =
    notification?.buttonTarget === 'form' && notification?.formUrl
      ? notification.formUrl
      : notification?.url;
  if (!targetUrl) return '';
  const inlineStyle = buildEmailAnchorInlineStyle({
    backgroundColor: notification.buttonColor,
    borderColor:
      notification.buttonBorderColor || notification.buttonColor,
    textColor: notification.buttonTextColor,
    shape: notification.buttonShape,
  });
  const label = escapeEmailTemplateHtml(
    normalizeHubupNotificationButtonLabel(notification.buttonLabel),
  );
  return `
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:18px 0;">
  <tr>
    <td align="center" style="padding:0;">
      <a href="${escapeEmailTemplateHtml(targetUrl)}" target="_blank" rel="noopener" style="${inlineStyle}box-shadow:0 6px 14px rgba(17,24,39,0.12);">
        ${label}
      </a>
    </td>
  </tr>
</table>`.trim();
}

export function renderCampaignEmailTemplate(templateHtml, replacements = {}) {
  const template = campaignEmailHtmlForPreview(templateHtml);
  const usesHubup = templateUsesHubupVariable(template);
  const includeHubup =
    replacements.includeHubup !== undefined
      ? replacements.includeHubup
      : usesHubup;
  const notification =
    includeHubup && usesHubup ? replacements.hubupNotification || null : null;
  const includeProductCard = replacements.includeProductCard === true;
  const productCardHtml = replacements.productCardHtml || '';
  let out = stripLegacyMessageCloserVariable(template).replace(
    /%hubup_notification_button%/g,
    includeHubup && usesHubup
      ? buildHubupNotificationButtonPreview(notification)
      : '',
  );
  if (includeProductCard && productCardHtml) {
    out = `${out}${productCardHtml}`;
  }
  return out;
}

export function campaignEmailTemplateId(template) {
  return String(template?._id ?? template?.id ?? '').trim();
}

export function campaignEmailTemplatesFromCampaign(campaign) {
  return Array.isArray(campaign?.emailTemplates) ? campaign.emailTemplates : [];
}

export function latestCampaignEmailTemplate(campaign) {
  const templates = campaignEmailTemplatesFromCampaign(campaign);
  if (templates.length === 0) return null;
  return [...templates].sort((a, b) => {
    const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (ta !== tb) return tb - ta;
    return templates.indexOf(b) - templates.indexOf(a);
  })[0];
}

export function findCampaignEmailTemplate(campaign, templateId) {
  const id = String(templateId ?? '').trim();
  if (!id) return null;
  return (
    campaignEmailTemplatesFromCampaign(campaign).find(
      (template) => campaignEmailTemplateId(template) === id,
    ) || null
  );
}

export function campaignWithEmailTemplateSource(campaign, source) {
  return {
    ...(campaign || {}),
    emailTemplateSubject: source?.emailTemplateSubject || '',
    emailTemplateHtml: source?.emailTemplateHtml || '',
    emailTemplateBannerUrl: source?.emailTemplateBannerUrl || '',
    emailTemplateBannerKey: source?.emailTemplateBannerKey || '',
    emailTemplateBannerOriginalName:
      source?.emailTemplateBannerOriginalName || '',
    hubupEmailNotificationMessageId:
      source?.hubupEmailNotificationMessageId || '',
    hubupEmailNotificationTitle: source?.hubupEmailNotificationTitle || '',
    hubupEmailNotificationFormId: source?.hubupEmailNotificationFormId || '',
    hubupEmailNotificationButtonTarget:
      source?.hubupEmailNotificationButtonTarget ||
      DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
    hubupEmailNotificationButtonLabel:
      source?.hubupEmailNotificationButtonLabel ||
      DEFAULT_HUBUP_NOTIFICATION_BUTTON_LABEL,
    hubupEmailNotificationButtonColor:
      source?.hubupEmailNotificationButtonColor ||
      DEFAULT_HUBUP_NOTIFICATION_BUTTON_COLOR,
  };
}

export function isCampaignEmailSourceSendable(source) {
  return String(source?.emailTemplateHtml || '').trim().length > 0;
}

export function publishedHubupNotificationsFromCampaign(campaign) {
  const history = Array.isArray(campaign?.hubupNotificationHistory)
    ? campaign.hubupNotificationHistory
    : [];
  return history
    .filter((item) => String(item?.messageId ?? '').trim())
    .map((item) => {
      const messageId = String(item.messageId).trim();
      return {
        messageId,
        formId: String(item.formId || '').trim(),
        title: String(item.title || 'Notification HubUP').trim(),
        topicTitle: String(item.topicTitle || '').trim(),
        sentAt: item.sentAt,
        url: buildHubupNotificationUrl(messageId),
        formUrl: buildHubupFormUrl(messageId),
        buttonLabel: normalizeHubupNotificationButtonLabel(
          campaign?.hubupEmailNotificationButtonLabel,
        ),
        buttonColor: normalizeHubupNotificationButtonColor(
          campaign?.hubupEmailNotificationButtonColor,
        ),
        buttonTarget:
          campaign?.hubupEmailNotificationButtonTarget ||
          DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
      };
    });
}

export function publishedHubupNotificationsFromMessages(messages, campaign = {}) {
  return Array.isArray(messages)
    ? messages
        .map((item) => {
          const messageId = String(
            item?.messageId ?? item?.id ?? item?._id ?? '',
          ).trim();
          if (!messageId) return null;
          return {
            messageId,
            formId: String(item?.formId || '').trim(),
            title: String(item?.title || 'Notification HubUP').trim(),
            topicTitle: String(
              item?.topicTitle || item?.topic?.title || '',
            ).trim(),
            sentAt: item?.sentAt || item?.createdAt || item?.updatedAt || null,
            url: buildHubupNotificationUrl(messageId),
            formUrl: buildHubupFormUrl(messageId),
            buttonLabel: normalizeHubupNotificationButtonLabel(
              campaign?.hubupEmailNotificationButtonLabel,
            ),
            buttonColor: normalizeHubupNotificationButtonColor(
              campaign?.hubupEmailNotificationButtonColor,
            ),
            buttonTarget:
              campaign?.hubupEmailNotificationButtonTarget ||
              DEFAULT_HUBUP_NOTIFICATION_BUTTON_TARGET,
          };
        })
        .filter(Boolean)
    : [];
}

export function mergePublishedHubupNotifications(...lists) {
  const byId = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const messageId = String(item?.messageId ?? '').trim();
      if (!messageId) continue;
      const existing = byId.get(messageId);
      if (existing) {
        byId.set(messageId, {
          ...existing,
          ...item,
          title: existing.title || item.title,
          topicTitle: existing.topicTitle || item.topicTitle,
          sentAt: existing.sentAt || item.sentAt,
          url: existing.url || item.url,
          formId: existing.formId || item.formId,
          formUrl: existing.formUrl || item.formUrl,
          buttonLabel: existing.buttonLabel || item.buttonLabel,
          buttonColor: existing.buttonColor || item.buttonColor,
          buttonTarget: existing.buttonTarget || item.buttonTarget,
        });
        continue;
      }
      byId.set(messageId, item);
    }
  }
  return [...byId.values()].sort((a, b) => {
    const ta = a?.sentAt ? new Date(a.sentAt).getTime() : 0;
    const tb = b?.sentAt ? new Date(b.sentAt).getTime() : 0;
    return tb - ta;
  });
}

export function resolveCampaignEmailHubupNotification(campaign) {
  const selectedMessageId = String(
    campaign?.hubupEmailNotificationMessageId ?? '',
  ).trim();
  if (!selectedMessageId) return null;
  return (
    publishedHubupNotificationsFromCampaign(campaign).find(
      (item) => item.messageId === selectedMessageId,
    ) || null
  );
}
